'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $getSelection,
    $isRangeSelection,
    $createTextNode,
    COMMAND_PRIORITY_HIGH,
    KEY_ESCAPE_COMMAND,
} from 'lexical'
import { FiFilm, FiFileText, FiPause, FiLayers } from 'react-icons/fi'
import { TbMoodSmile } from 'react-icons/tb'
import { $createMentionNode } from './MentionNode'
import { $getContextAtCursor } from './editorContext'
import MentionDropdown from './MentionDropdown'

const COMMAND_ICONS = {
    segment: <FiLayers />,
    scene: <FiFilm />,
    description: <FiFileText />,
    break: <FiPause />,
    emotion: <TbMoodSmile />
}

function getQueryAndRange(editor) {
    return editor.getEditorState().read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null

        const anchor = selection.anchor
        if (anchor.type !== 'text') return null

        const anchorNode = anchor.getNode()
        const text = anchorNode.getTextContent().slice(0, anchor.offset)

        const slashIndex = text.lastIndexOf('/')
        if (slashIndex === -1) return null

        const charBefore = slashIndex > 0 ? text[slashIndex - 1] : ' '
        if (charBefore !== ' ' && charBefore !== '\n' && slashIndex !== 0) return null

        const query = text.slice(slashIndex + 1)
        return { query, slashIndex, anchorOffset: anchor.offset, anchorKey: anchor.key }
    })
}

const DIALOGUE_ONLY_COMMANDS = new Set(['break', 'emotion'])

const CONTEXT_ALLOWED_COMMANDS = {
    // character: new Set(['break', 'emotion']),
    character: new Set([]),
    description: new Set([]),
    scene: new Set([]),
    segment: new Set([]),
}

function getCommandsForContext(commands, mode) {
    if (mode && CONTEXT_ALLOWED_COMMANDS[mode]) {
        const allowed = CONTEXT_ALLOWED_COMMANDS[mode]
        return commands.filter(cmd => allowed.has(cmd.id))
    }
    return commands.filter(cmd => !DIALOGUE_ONLY_COMMANDS.has(cmd.id))
}

export default function SlashCommandPlugin({ commands = [], forcedMode = null }) {
    const [editor] = useLexicalComposerContext()
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [anchorRect, setAnchorRect] = useState(null)
    const [editorMode, setEditorMode] = useState(null)
    const matchRef = useRef(null)

    const effectiveMode = forcedMode ?? editorMode
    const contextualCommands = getCommandsForContext(commands, effectiveMode)

    const filteredCommands = contextualCommands
        .filter(cmd => cmd.label.toLowerCase().includes(query.toLowerCase()))
        .map(cmd => ({ ...cmd, icon: cmd.icon || COMMAND_ICONS[cmd.id] }))

    const updateDropdownPosition = useCallback(() => {
        const domSelection = window.getSelection()
        if (!domSelection || domSelection.rangeCount === 0) return

        const range = domSelection.getRangeAt(0)
        const rect = range.getBoundingClientRect()
        setAnchorRect(rect)
    }, [])

    const close = useCallback(() => {
        setIsOpen(false)
        setQuery('')
        matchRef.current = null
    }, [])

    const handleSelect = useCallback((item) => {
        editor.update(() => {
            const match = matchRef.current
            if (!match) return

            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return

            const anchor = selection.anchor
            const anchorNode = anchor.getNode()
            const text = anchorNode.getTextContent()

            const before = text.slice(0, match.slashIndex)
            const after = text.slice(match.anchorOffset)

            anchorNode.setTextContent(before)

            const mentionNode = $createMentionNode(
                'command',
                item.id,
                item.label,
                item.data || {}
            )

            if (before.length > 0) {
                anchorNode.insertAfter(mentionNode)
            } else {
                anchorNode.replace(mentionNode)
            }

            const trailingText = $createTextNode(after.length > 0 ? after : ' ')
            mentionNode.insertAfter(trailingText)
            trailingText.select(0, 0)
        })
        close()
    }, [editor, close])

    useEffect(() => {
        return editor.registerCommand(
            KEY_ESCAPE_COMMAND,
            () => {
                if (isOpen) {
                    close()
                    return true
                }
                return false
            },
            COMMAND_PRIORITY_HIGH
        )
    }, [editor, isOpen, close])

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const result = getQueryAndRange(editor)
                if (result) {
                    if (!forcedMode) {
                        const context = $getContextAtCursor()
                        setEditorMode(context)
                    }
                    matchRef.current = result
                    setQuery(result.query)
                    setIsOpen(true)
                    setTimeout(updateDropdownPosition, 0)
                } else {
                    if (isOpen) close()
                }
            })
        })
    }, [editor, isOpen, close, updateDropdownPosition, forcedMode])

    if (!isOpen || contextualCommands.length === 0) return null

    return (
        <MentionDropdown
            anchorRect={anchorRect}
            items={filteredCommands}
            onSelect={handleSelect}
            onClose={close}
            emptyMessage="No commands found"
        />
    )
}
